/**
 * @file scheduledSide.ts
 * @brief Scheduled meetings: "N internal · N external", and a saved
 *        participant → the edit form's shape.
 *
 * @details
 * DES 10-10-2026: internal = the organiser + the organiser's company; anyone
 * else is external, VERBLIZR user or not. The backend decides it when the
 * participants are saved (BACKEND features/meetings/participantSide.mjs) and
 * returns `isExternal`; this counts that flag, the same as the phone
 * (FRONTEND scheduledSetupParticipant.ts).
 *
 * Edit: a company member travels by account id and stays shared; anyone
 * else stays external and unshared (a registered one keeps
 * `isRegisteredUser`, so the Share toggle is offered). Scheduled meetings
 * store no share choice; this is what a fresh setup gives the same people.
 * Starting a scheduled meeting keeps its own mapping (no change in who is
 * shared).
 *
 * No imports: node:test runs it directly.
 *
 * @author DES
 * @date 10-10-2026
 */

export type SavedParticipant = {
  id?: string | null;
  userId?: string | null;
  email?: string | null;
  name?: string | null;
  isExternal?: boolean;
  profileImageUrl?: string | null;
};

export type EditParticipant = {
  id?: string;
  name: string;
  email: string;
  isExternal: boolean;
  shared?: boolean;
  profileImageUrl?: string;
  isRegisteredUser?: boolean;
};

export function sideCounts(people: { isExternal?: boolean }[]): { internal: number; external: number } {
  const external = people.filter((p) => p.isExternal === true).length;
  return { internal: people.length - external, external };
}

export function scheduledToEditParticipant(p: SavedParticipant): EditParticipant {
  const email = (p.email || "").trim();
  const name = (p.name || email || "Guest").trim();
  if (p.userId && p.isExternal !== true) {
    return {
      id: String(p.userId),
      name,
      // B48: no stand-in address (DES 02-11-2026); a member travels by id.
      email,
      isExternal: false,
      shared: true,
      profileImageUrl: p.profileImageUrl ?? undefined,
    };
  }
  return {
    id: p.userId && email ? `ext_${email.toLowerCase()}` : p.id || undefined,
    name,
    email,
    isExternal: true,
    shared: false,
    ...(p.userId ? { isRegisteredUser: true, profileImageUrl: p.profileImageUrl ?? undefined } : {}),
  };
}

/**
 * Agenda notes sent with "Schedule" (DES 10-10-2026: the web dropped them;
 * the phone sends both fields): the list, plus its first id for a server that
 * only knows one note. A Notes meeting has no agenda.
 */
export function scheduleAgenda(
  prepNoteIds: string[] | undefined,
  isNotes: boolean,
): { prepNoteIds: string[]; prepNoteId: string | null } {
  const ids = isNotes ? [] : (prepNoteIds ?? []);
  return { prepNoteIds: ids, prepNoteId: ids[0] ?? null };
}

/**
 * Agenda notes a scheduled meeting carries into the recorder on "Start now"
 * (the phone's MyMeetingsScreen does the same). Only the organiser gets them
 * from the API; anyone else starts with none.
 */
export function startAgenda(m: {
  prep_note_id?: string | null;
  prep_note_ids?: string[] | null;
}): { prepNoteId?: string; prepNoteIds?: string[] } {
  return {
    ...(m.prep_note_id ? { prepNoteId: m.prep_note_id } : {}),
    ...(m.prep_note_ids?.length ? { prepNoteIds: m.prep_note_ids } : {}),
  };
}

// ----------------------------------------------------------------------
// B48 (DES 02-11-2026): Edit meeting before, during and after it.
// ----------------------------------------------------------------------

/** A stand-in address older builds stored for a guest with no email. */
export function isStandInEmail(email?: string | null): boolean {
  return /@external\.local$/i.test(String(email ?? "").trim());
}

/** The email to show, or "" for none / a stand-in (show "Guest" then). */
export function shownEmail(email?: string | null): string {
  const e = String(email ?? "").trim();
  return e && !isStandInEmail(e) ? e : "";
}

export type RosterEntry = {
  id?: string | null;
  userId?: string | null;
  email?: string | null;
  name?: string | null;
  isExternal?: boolean;
  accessRemoved?: boolean | null;
  [key: string]: unknown;
};

/** A row of the Edit meeting form: one already on the meeting, or new. */
export type EditRosterRow = {
  key: string;
  name: string;
  /** Shown under the name; "" = "Guest". */
  email: string;
  isExternal: boolean;
  profileImageUrl?: string | null;
  /** The entry as the server sent it, echoed back unchanged on save so the
   *  server matches it (a stand-in address included). */
  saved?: RosterEntry;
  /** New: a company member by id, or someone new by name + email. */
  add?: { userId: string } | { name: string; email: string };
};

/** People shown in the form: never anyone whose access was taken back
 *  (B45), and not the organizer (always on the meeting). */
export function editRosterRows(participants: RosterEntry[], organizerId?: string | null): EditRosterRow[] {
  return (participants ?? [])
    .filter((p) => p.accessRemoved !== true)
    .filter((p) => !(organizerId && p.userId && String(p.userId) === String(organizerId)))
    .map((p, i) => ({
      key: String(p.id || p.userId || p.email || `row-${i}`),
      name: String(p.name || "").trim() || "Guest",
      email: shownEmail(p.email),
      isExternal: p.isExternal === true,
      profileImageUrl: (p.profileImageUrl as string | null | undefined) ?? null,
      saved: p,
    }));
}

/** The `participants` array for PUT /api/meetings/:id. */
export function rosterBody(rows: EditRosterRow[]): Record<string, unknown>[] {
  return rows.map((r) => {
    if (r.saved) {
      const { accessRemoved: _a, profileImageUrl: _p, hasAccount: _h, ...entry } = r.saved;
      return entry;
    }
    const add = r.add!;
    return "userId" in add
      ? { userId: add.userId, name: r.name, isExternal: false }
      : { name: add.name, email: add.email, isExternal: true, shared: false };
  });
}

export type MeetingEditForm = {
  title: string;
  /** ISO start; sent only while the time is open and it changed. */
  scheduledAt?: string;
  rows: EditRosterRow[];
  prepNoteIds: string[];
};

/**
 * The PUT body: only the groups that are open now (`editable`), so a locked
 * field is never sent; the server also ignores a locked field sent back
 * unchanged, and refuses a changed one (409 MEETING_FIELD_LOCKED).
 */
export function meetingEditBody(
  meeting: { title?: string | null; scheduled_at?: string | null; prep_note_ids?: string[] | null; prep_note_id?: string | null },
  editable: { title?: boolean; participants?: boolean; agenda?: boolean; time?: boolean },
  form: MeetingEditForm,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const title = form.title.replace(/\s+/g, " ").trim();
  if (editable.title && title && title !== String(meeting.title ?? "").trim()) body.title = title;
  if (editable.participants) body.participants = rosterBody(form.rows);
  if (editable.time && form.scheduledAt) {
    const before = new Date(String(meeting.scheduled_at ?? "")).getTime();
    if (new Date(form.scheduledAt).getTime() !== before) body.scheduledAt = form.scheduledAt;
  }
  if (editable.agenda) {
    const stored = meeting.prep_note_ids?.length
      ? meeting.prep_note_ids
      : meeting.prep_note_id
        ? [meeting.prep_note_id]
        : [];
    const same =
      stored.length === form.prepNoteIds.length && stored.every((id, i) => id === form.prepNoteIds[i]);
    if (!same) {
      body.prepNoteIds = form.prepNoteIds;
      body.prepNoteId = form.prepNoteIds[0] ?? null;
    }
  }
  return body;
}
